// ============================================================
// 📁 آدرس این فایل در پروژه (فایل جدید):
//    src/pages/warehouse/CustomerReceiptPage.tsx
//
// «رسید مشتری» — در منوی گدام، بلافاصله بعد از «رسید جنس»
//   مرحله‌ی آخر پرونده: فروش به مشتری ← رسیدن بار به مشتری ← بستن پرونده
//   • لیست پرونده‌هایی که جنسشان در گدام رسید شده
//   • داخل هر پرونده: «فروش جدید» (فرم ساده)، رسید مشتری، سود/زیان، بستن پرونده
// ============================================================

import React, { useState, useEffect, useCallback } from "react";
import { useTheme, DARK, LIGHT } from "../../components/Layout";
import { transitAPI, journalAPI } from "../../services/api";
import { CaseSalesView, ReceiptForm } from "../tracking/TransitSales";
import { Search, ArrowRight, Truck } from "lucide-react";

const FONT = "'Vazirmatn','Segoe UI',sans-serif";
const fmt = (n: any) => new Intl.NumberFormat("en-US", { minimumFractionDigits: 0, maximumFractionDigits: 3 }).format(Number(n) || 0);
const f2 = (n: any) => new Intl.NumberFormat("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(Number(n) || 0);

export default function CustomerReceiptPage() {
  const { dark } = useTheme();
  const T = dark ? DARK : LIGHT;
  const [board, setBoard] = useState<any>(null);
  const [err, setErr] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(true);
  const [selId, setSelId] = useState<number | null>(null);
  const [view, setView] = useState<string>("open");
  const [mainTab, setMainTab] = useState<"cases" | "foreign">("cases");
  const [fRows, setFRows] = useState<any[]>([]);
  const [fCan, setFCan] = useState<any>({});
  const [fLoading, setFLoading] = useState<boolean>(false);
  const [rcLink, setRcLink] = useState<any>(null);
  const [q, setQ] = useState<string>("");
  const [toast, setToast] = useState<{ text: string; ok: boolean }>({ text: "", ok: true });
  const showToast = (text: string, ok: boolean = true) => { setToast({ text, ok }); setTimeout(() => setToast({ text: "", ok: true }), 3500); };

  const load = useCallback(async () => {
    try {
      const r = await transitAPI.caseBoard(0);
      if (r?.success) { setBoard(r); setErr(""); } else setErr(r?.message || "خطا");
    } catch { setErr("ارتباط با سرور برقرار نشد"); }
    setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);

  const loadForeign = useCallback(async () => {
    setFLoading(true);
    try {
      const r = await journalAPI.foreignSaleReceipts({ status: "all", keyword: q || undefined });
      if (r?.success) { setFRows(r.receipts || []); setFCan(r.can || {}); }
    } catch { /* ignore */ }
    setFLoading(false);
  }, [q]);
  useEffect(() => { if (mainTab === "foreign") loadForeign(); }, [mainTab, loadForeign]);

  const delReceipt = async (linkId: number) => {
    if (!window.confirm("رسید مشتری حذف شود؟")) return;
    const r = await journalAPI.caseReceiptDelete(linkId);
    if (r?.success) { showToast("رسید حذف شد"); loadForeign(); } else showToast(r?.message || "خطا", false);
  };
  const linkOf = (row: any, it: any) => ({
    link_id: it.link_id, qty: it.qty, unit: it.unit, currency: row.customer_currency, customer: row.customer_name,
    product_name: it.product_name, serial: row.sale_serial, revenue_cur: it.total_price,
    receipt: it.receipt_id ? { date: it.received_date, qty: it.received_qty, notes: it.receipt_notes, attachment: it.receipt_attachment } : null,
  });

  const cases: any[] = board?.cases || [];
  const sel = selId !== null ? cases.find((c: any) => c.transit.id === selId) : null;

  const pendingOf = (c: any) => c.sales.totals.no_receipt || 0;
  const remainOf = (c: any) => (c.stock || []).reduce((a: number, s: any) => a + Math.max(0, Number(s.remaining) || 0), 0);
  const totalPending = cases.filter((c: any) => !c.closed).reduce((a: number, c: any) => a + pendingOf(c), 0);

  const shown = cases.filter((c: any) => {
    if (view === "open" && c.closed) return false;
    if (view === "closed" && !c.closed) return false;
    if (q) {
      const hay = `${c.transit.serial} ${c.purchase.serial} ${c.purchase.vendor_name || ""} ${(c.stock || []).map((s: any) => s.product_name).join(" ")} ${(c.sales.links || []).map((l: any) => l.customer).join(" ")}`.toLowerCase();
      if (!hay.includes(q.toLowerCase())) return false;
    }
    return true;
  });

  const surface = dark ? "rgba(255,255,255,0.03)" : "#fff";
  const tabBtn = (id: string, label: string): React.CSSProperties => ({ padding: "8px 18px", borderRadius: 10, border: `1.5px solid ${view === id ? "#10b981" : T.cardBorder}`, background: view === id ? "rgba(16,185,129,0.12)" : "transparent", color: view === id ? "#059669" : T.subtext, fontWeight: 800, fontSize: 12.5, cursor: "pointer", fontFamily: FONT });

  return (
    <div dir="rtl" style={{ fontFamily: FONT }}>
      {toast.text && <div style={{ position: "fixed", top: 20, left: "50%", transform: "translateX(-50%)", padding: "12px 24px", borderRadius: 12, background: toast.ok ? "#10b981" : "#ef4444", color: "#fff", fontWeight: 800, fontSize: 13, zIndex: 3000, boxShadow: "0 8px 30px rgba(0,0,0,0.25)" }}>{toast.text}</div>}

      {loading && <div style={{ textAlign: "center", padding: 60, color: T.subtext }}>در حال بارگذاری...</div>}
      {!loading && err && <div style={{ padding: 20, borderRadius: 14, border: `1px solid ${T.cardBorder}`, color: "#dc2626", fontSize: 13 }}>{err}</div>}

      {!loading && !err && !sel && (
        <div style={{ display: "flex", gap: 8, marginBottom: 16 }}>
          {([["cases", "🛒 پرونده‌های خرید"], ["foreign", "🌍 فروش خارجی (ترانزیت)"]] as [string, string][]).map(([k, lb]) => (
            <button key={k} onClick={() => setMainTab(k as any)} style={{ flex: 1, padding: "12px 16px", borderRadius: 12, cursor: "pointer", fontFamily: FONT, fontSize: 13.5, fontWeight: 800, border: `2px solid ${mainTab === k ? "#10b981" : T.cardBorder}`, background: mainTab === k ? "rgba(16,185,129,0.12)" : "transparent", color: mainTab === k ? "#059669" : T.subtext }}>{lb}</button>
          ))}
        </div>
      )}

      {/* فروش‌های خارجی در ترانزیت */}
      {!loading && !err && !sel && mainTab === "foreign" && (
        <div>
          <div style={{ fontSize: 12.5, color: T.subtext, marginBottom: 12 }}>فروش خارجی بعد از رسیدن بار به مشتری، اینجا «رسید مشتری» می‌گیرد. اگر کمتر رسیده باشد، سیستم کسری را خودکار از حساب مشتری کم می‌کند.</div>
          <div style={{ position: "relative", marginBottom: 14 }}>
            <Search size={15} color={T.subtext} style={{ position: "absolute", right: 12, top: 12 }} />
            <input value={q} onChange={e => setQ(e.target.value)} placeholder="جستجو: شماره فروش، مشتری، شماره ترانزیت..." style={{ width: "100%", padding: "10px 36px 10px 14px", borderRadius: 10, border: `1.5px solid ${T.border}`, background: T.input, color: T.text, fontSize: 13, fontFamily: FONT, boxSizing: "border-box", outline: "none" }} />
          </div>
          {fLoading && <div style={{ textAlign: "center", padding: 40, color: T.subtext }}>در حال بارگذاری...</div>}
          {!fLoading && fRows.length === 0 && <div style={{ padding: 40, textAlign: "center", color: T.subtext, fontSize: 13.5, border: `1px dashed ${T.cardBorder}`, borderRadius: 16 }}>هنوز فروش خارجی‌ای در ترانزیت نیست.</div>}
          <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
            {fRows.map((row: any) => {
              const done = row.transit_status === "completed";
              const items: any[] = row.items || [];
              const pend = items.filter((it: any) => !it.receipt_id).length;
              return (
                <div key={row.transit_id} style={{ padding: 18, borderRadius: 16, border: `1px solid ${T.cardBorder}`, borderTop: `4px solid ${!done ? "#f59e0b" : pend > 0 ? "#2563eb" : "#10b981"}`, background: surface }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 8, marginBottom: 10 }}>
                    <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
                      <span style={{ fontWeight: 900, color: T.text, fontSize: 15 }}><Truck size={14} style={{ marginLeft: 6 }} />{row.transit_serial}</span>
                      <span style={{ fontFamily: "monospace", fontWeight: 800, color: "#059669" }}>{row.sale_serial}</span>
                      <b style={{ color: T.text }}>{row.customer_name}</b>
                    </div>
                    <span style={{ padding: "4px 12px", borderRadius: 8, fontSize: 12, fontWeight: 800, background: !done ? "rgba(245,158,11,0.15)" : pend > 0 ? "rgba(37,99,235,0.12)" : "rgba(16,185,129,0.12)", color: !done ? "#b45309" : pend > 0 ? "#2563eb" : "#059669" }}>{!done ? "هنوز در راه است" : pend > 0 ? `${pend} رسید مانده` : "همه رسید شد"}</span>
                  </div>
                  {items.map((it: any) => (
                    <div key={it.link_id} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 8, padding: "10px 0", borderTop: `1px solid ${T.cardBorder}` }}>
                      <div style={{ fontSize: 13, color: T.text }}>📦 <b>{it.product_name}</b> — فروخته‌شده {fmt(it.qty)} {it.unit}
                        {it.receipt_id && <span style={{ marginRight: 10, color: "#059669", fontWeight: 800 }}>✓ رسیده {fmt(it.received_qty)} {it.unit}</span>}
                      </div>
                      <div style={{ display: "flex", gap: 8 }}>
                        {done && (fCan.edit || (it.receipt_id && fCan.approve)) && <button onClick={() => setRcLink(linkOf(row, it))} style={{ padding: "8px 16px", borderRadius: 9, border: "none", cursor: "pointer", fontFamily: FONT, fontSize: 12.5, fontWeight: 800, color: "#fff", background: it.receipt_id ? "linear-gradient(135deg,#2563eb,#1d4ed8)" : "linear-gradient(135deg,#10b981,#059669)" }}>{it.receipt_id ? "ویرایش رسید" : "ثبت رسید مشتری"}</button>}
                        {it.receipt_id && fCan.approve && <button onClick={() => delReceipt(it.link_id)} style={{ padding: "8px 14px", borderRadius: 9, border: "none", cursor: "pointer", fontFamily: FONT, fontSize: 12.5, fontWeight: 800, background: "rgba(239,68,68,0.12)", color: "#ef4444" }}>حذف رسید</button>}
                      </div>
                    </div>
                  ))}
                </div>
              );
            })}
          </div>
          {rcLink && <ReceiptForm link={rcLink} onClose={() => setRcLink(null)} onSaved={() => { setRcLink(null); loadForeign(); }} T={T} dark={dark} showToast={showToast} />}
        </div>
      )}

      {/* داخل یک پرونده */}
      {!loading && !err && sel && (
        <div>
          <button onClick={() => setSelId(null)} style={{ display: "inline-flex", alignItems: "center", gap: 6, marginBottom: 14, padding: "8px 16px", borderRadius: 10, border: `1px solid ${T.cardBorder}`, background: "transparent", color: T.text, cursor: "pointer", fontWeight: 800, fontSize: 12.5, fontFamily: FONT }}><ArrowRight size={14} /> همه‌ی پرونده‌ها</button>
          <div style={{ padding: 22, borderRadius: 18, border: `1px solid ${T.cardBorder}`, background: surface }}>
            <div style={{ fontSize: 17, fontWeight: 900, color: T.text, marginBottom: 12 }}>🚚 پرونده {sel.transit.serial}</div>
            <CaseSalesView c={sel} canEdit={!!board.can?.edit} canApprove={!!board.can?.approve} reload={load} T={T} dark={dark} showToast={showToast} />
          </div>
        </div>
      )}

      {/* لیست پرونده‌ها */}
      {!loading && !err && !sel && mainTab === "cases" && (
        <div>
          <div style={{ marginBottom: 16 }}>
            <div style={{ fontSize: 16, fontWeight: 900, color: T.text }}>🧾 رسید مشتری</div>
            <div style={{ fontSize: 12.5, color: T.subtext, marginTop: 4 }}>پرونده‌ای را باز کنید، «فروش جدید» را بزنید (مشتری، مقدار، قیمت) و بعد از رسیدن بار به مشتری، «رسید مشتری» را ثبت کنید. سود و زیان خودکار حساب می‌شود.</div>
          </div>

          {totalPending > 0 && <div style={{ padding: "10px 16px", borderRadius: 12, background: "rgba(245,158,11,0.12)", color: "#b45309", fontWeight: 800, fontSize: 13, marginBottom: 14 }}>● {fmt(totalPending)} فروش منتظر «رسید مشتری» است</div>}

          <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center", marginBottom: 16 }}>
            <button onClick={() => setView("open")} style={tabBtn("open", "در جریان")}>در جریان</button>
            <button onClick={() => setView("closed")} style={tabBtn("closed", "بسته‌شده")}>بسته‌شده</button>
            <button onClick={() => setView("all")} style={tabBtn("all", "همه")}>همه</button>
            <div style={{ position: "relative", flex: 1, minWidth: 200 }}>
              <Search size={15} color={T.subtext} style={{ position: "absolute", right: 12, top: 12 }} />
              <input value={q} onChange={e => setQ(e.target.value)} placeholder="جستجو: شماره پرونده، جنس، مشتری، فروشنده..." style={{ width: "100%", padding: "10px 36px 10px 14px", borderRadius: 10, border: `1.5px solid ${T.border}`, background: T.input, color: T.text, fontSize: 13, fontFamily: FONT, boxSizing: "border-box", outline: "none" }} />
            </div>
          </div>

          {shown.length === 0 && <div style={{ padding: 40, textAlign: "center", color: T.subtext, fontSize: 13.5, border: `1px dashed ${T.cardBorder}`, borderRadius: 16 }}>{cases.length === 0 ? "هنوز پرونده‌ای که جنسش در گدام رسید شده باشد وجود ندارد. اول در «رسید جنس» بار را رسید کنید." : "پرونده‌ای با این فیلتر پیدا نشد."}</div>}

          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(320px,1fr))", gap: 14 }}>
            {shown.map((c: any) => {
              const t = c.sales.totals; const pend = pendingOf(c); const rem = remainOf(c);
              const accent = c.closed ? "#64748b" : pend > 0 ? "#f59e0b" : "#10b981";
              return (
                <div key={c.transit.id} onClick={() => setSelId(c.transit.id)} style={{ cursor: "pointer", padding: 18, borderRadius: 16, border: `1px solid ${T.cardBorder}`, borderTop: `4px solid ${accent}`, background: surface, transition: "transform 0.1s" }}
                  onMouseEnter={e => (e.currentTarget.style.transform = "translateY(-2px)")}
                  onMouseLeave={e => (e.currentTarget.style.transform = "translateY(0)")}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
                    <div style={{ fontSize: 15, fontWeight: 900, color: T.text, display: "flex", alignItems: "center", gap: 6 }}><Truck size={15} color={accent} /> {c.transit.serial}</div>
                    {c.closed ? <span style={{ fontSize: 11, fontWeight: 800, color: "#64748b", background: "rgba(100,116,139,0.12)", padding: "3px 10px", borderRadius: 8 }}>بسته شده</span> : pend > 0 ? <span style={{ fontSize: 11, fontWeight: 800, color: "#b45309", background: "rgba(245,158,11,0.15)", padding: "3px 10px", borderRadius: 8 }}>{pend} رسید مشتری مانده</span> : <span style={{ fontSize: 11, fontWeight: 800, color: "#059669", background: "rgba(16,185,129,0.12)", padding: "3px 10px", borderRadius: 8 }}>باز</span>}
                  </div>
                  <div style={{ fontSize: 12, color: T.subtext, marginBottom: 8 }}>خرید {c.purchase.serial}{c.purchase.vendor_name ? ` — ${c.purchase.vendor_name}` : ""}</div>
                  {(c.stock || []).map((s: any) => (
                    <div key={s.product_id} style={{ fontSize: 12.5, color: T.text, lineHeight: 1.9 }}>
                      <b>{s.product_name}</b>: رسید {fmt(s.received)} — فروخته {fmt(s.sold)} — <span style={{ fontWeight: 900, color: Number(s.remaining) > 0.00005 ? "#10b981" : T.subtext }}>مانده {fmt(s.remaining)} {s.unit}</span>
                    </div>
                  ))}
                  <div style={{ display: "flex", justifyContent: "space-between", marginTop: 10, paddingTop: 10, borderTop: `1px solid ${T.cardBorder}`, fontSize: 12 }}>
                    <span style={{ color: T.subtext }}>{(c.sales.links || []).length} فروش{rem <= 0.00005 && (c.sales.links || []).length > 0 ? " — همه فروخته شد" : ""}</span>
                    {(c.sales.links || []).length > 0 && <span style={{ fontWeight: 900, direction: "ltr", color: Number(t.profit_usd) >= 0 ? "#10b981" : "#ef4444" }}>{Number(t.profit_usd) >= 0 ? "+" : "-"}{f2(Math.abs(t.profit_usd))} USD</span>}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
